import React, { useState } from 'react';

const TEXT_ENTRY_SELECTOR = 'input:not([type="button"]):not([type="submit"]), textarea';

export interface InputModality {
  /** True after a pointer press inside the wrapper, until the keyboard takes over or focus leaves. */
  isPointerFocus: boolean;
  onPointerDown: () => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
  onBlur: (event: React.FocusEvent<HTMLElement>) => void;
}

/**
 * Tracks whether the focus inside a wrapper came from a pointer or the keyboard.
 * Text inputs match `:focus-visible` on a tap in Chromium and WebKit, so the CSS
 * pseudo-class alone cannot tell a tap from a Tab. Typing into a text field
 * never counts as keyboard navigation: only Tab, or a key press on another
 * control, hands the wrapper back to keyboard focus.
 */
export function useInputModality(): InputModality {
  const [isPointerFocus, setIsPointerFocus] = useState(false);

  return {
    isPointerFocus,
    onPointerDown: () => setIsPointerFocus(true),
    onKeyDown: (event) => {
      const isTextEntry = (event.target as HTMLElement).matches(TEXT_ENTRY_SELECTOR);
      if (event.key === 'Tab' || !isTextEntry) setIsPointerFocus(false);
    },
    onBlur: (event) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
        setIsPointerFocus(false);
      }
    },
  };
}
